import { Box, Grid, Tooltip } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { User } from 'modules/users/models/User'
import { UserService } from '../../modules/users/services/UserService'
import { LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import { getUserContainer } from '../../container/user-module'
import styles from './dashboard.module.css'
import { useTranslation } from 'react-i18next'
import iconCalendar from '../../assets/dashboard/calendar.svg'
import iconPatient from '../../assets/dashboard/patient.svg'
import iconStatistics from '../../assets/dashboard/statistics.svg'
import iconEdit from '../../assets/table_icons/edit.svg'
import { Statistics } from '../statistics'
import { navigate } from '@reach/router'
import { ROUTE_USERS_CREATE } from '../../routes/routes-constants'
import { WearableAssignment } from './WearableAssignment'
import { Skeleton } from '@material-ui/lab'
import { FormStats, FormStat } from '../forms-analytics'
import { IAnalyticsService, ANALYTICS_SERVICE_KEY } from '../../modules/forms'
import { getFormContainer } from '../../container/form-module'
import { useUserRoles } from '../../modules/users/hooks/useUserRoles'
import { ILoggedUserService } from 'modules/users/services/LoggedUserService'
import FormStatsChart from 'components/form-stats-chart/FormStatsChart'
import { PageLayout } from 'pages/layout'
import { PatientProtocols } from '../protocols'

type DashboardProps = {
  id?: string
}

interface AlarmStatus {
  medication: boolean
  food: boolean
  sleep: boolean
}

const userContainer = getUserContainer()
const userService = userContainer.get<UserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const formContainer = getFormContainer()
const analyticsService = formContainer.get<IAnalyticsService>(ANALYTICS_SERVICE_KEY)

export const Dashboard = (props: DashboardProps) => {
  const { t } = useTranslation()
  const [user, setUser] = useState<User>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [medicationStats, setMedicationStats] = useState<FormStat[]>([])
  const [foodStats, setFoodStats] = useState<FormStat[]>([])
  const [sleepStats, setSleepStats] = useState<FormStat[]>([])
  const [alarmStatus, setAlarmStatus] = useState<AlarmStatus>({
    medication: false,
    food: false,
    sleep: false,
  })
  const [isLoadingStats, setIsLoadingStats] = useState<boolean>(true)

  const loggedUser = loggedUserService.get()
  const { canEditPatientData, canViewProtocols, canAssignProtocols } = useUserRoles(loggedUser)

  useEffect(() => {
    if (!props.id) {
      return
    }

    userService.getByID(props.id).subscribe((res) => {
      if (res) {
        setUser(res)
        setIsLoading(false)
      }
    })
  }, [props.id])

  useEffect(() => {
    if (!props.id) return

    const userId = props.id

    const loadStats = async () => {
      try {
        const [medication, food, sleep] = await Promise.all([
          analyticsService.getMedicationStats(userId).toPromise(),
          analyticsService.getFoodStats(userId).toPromise(),
          analyticsService.getSleepStats(userId).toPromise(),
        ])

        setMedicationStats(medication || [])
        setFoodStats(food || [])
        setSleepStats(sleep || [])
      } catch (error) {
        console.error('Error loading stats:', error)
      } finally {
        setIsLoadingStats(false)
      }
    }

    loadStats()
  }, [props.id])

  useEffect(() => {
    if (!props.id) return

    const userId = props.id

    const loadAlarmStatus = async () => {
      try {
        if (analyticsService.getAlarmStatus) {
          const alarmData = await analyticsService.getAlarmStatus(userId).toPromise()
          if (alarmData) {
            setAlarmStatus(alarmData)
          }
        } else {
          console.warn('getAlarmStatus method not available in analyticsService')
          setAlarmStatus({
            medication: false,
            food: false,
            sleep: false,
          })
        }
      } catch (error) {
        console.error('Error loading alarm status:', error)
        setAlarmStatus({
          medication: false,
          food: false,
          sleep: false,
        })
      }
    }

    loadAlarmStatus()
  }, [props.id])

  const calculateAge = (dob: Date) => {
    if (!dob) return ''
    const today = new Date()
    const birthDate = new Date(dob)
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  return (
    <PageLayout withCard={false}>
      <div className={styles.pageWrapper}>
        <Box className={styles.container}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={12}>
              {props.id && <WearableAssignment userId={props.id} />}
            </Grid>

            <Grid item xs={12} lg={12}>
              <Box className={styles.containerBoxStatistics}>
                <FormStats
                  medicationStats={medicationStats}
                  foodStats={foodStats}
                  sleepStats={sleepStats}
                  alarmStatus={alarmStatus}
                  userId={props.id || ''}
                  isLoading={isLoadingStats}
                />
              </Box>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Box className={styles.containerBoxLarge}>
                <Box className={styles.titleBoxPatientStatistics}>
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-start',
                      alignItems: 'flex-start',
                    }}
                  >
                    <p className={styles.titleText}>{t('patient')}</p>
                  </Box>
                  <img
                    className={styles.imageBox}
                    src={iconPatient}
                    alt={t('patientIcon')}
                    width={70}
                    height={70}
                  />
                </Box>

                <Box className={styles.patientInfoContainer}>
                  <Box className={styles.patientInfoRow}>
                    <span className={styles.patientInfoLabel}>{t('name')}:</span>
                    <span className={styles.patientInfoValue}>
                      {isLoading ? (
                        <Skeleton width={'8rem'} />
                      ) : (
                        `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
                      )}
                    </span>
                  </Box>

                  <Box className={styles.patientInfoRow}>
                    <span className={styles.patientInfoLabel}>{t('age')}:</span>
                    <span className={styles.patientInfoValue}>
                      {isLoading ? (
                        <Skeleton width={'4rem'} />
                      ) : user?.dob ? (
                        calculateAge(user.dob)
                      ) : (
                        ''
                      )}
                    </span>
                  </Box>

                  <Box className={styles.patientInfoRow}>
                    <span className={styles.patientInfoLabel}>{t('pathologies')}:</span>
                    <span className={styles.patientInfoValue}>
                      {isLoading ? (
                        <Skeleton width={'8rem'} />
                      ) : user?.patologies && user.patologies.length > 0 ? (
                        user.patologies
                          .map((pathology) => {
                            if (pathology === 'others') {
                              return user.otherPatology ? t(user.otherPatology) : t('others')
                            }
                            return t(pathology)
                          })
                          .join(', ')
                      ) : (
                        ''
                      )}
                    </span>
                  </Box>
                </Box>

                {canEditPatientData && (
                  <Box className={styles.editButtonContainer}>
                    <Tooltip
                      onClick={() => navigate(`${ROUTE_USERS_CREATE}/${user?.id}`)}
                      title={`${t('edit')}`}
                    >
                      <img
                        style={{ cursor: 'pointer' }}
                        className={styles.imageBox}
                        src={iconEdit}
                        alt={t('editIcon')}
                        width={38}
                        height={38}
                      />
                    </Tooltip>
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid item lg={8}>
              <Box className={styles.containerBoxStatistics}>
                <Box className={styles.titleBoxPatientStatistics}>
                  <p className={styles.titleText}>{t('statistics')}</p>
                  <img
                    style={{ marginRight: 40 }}
                    className={styles.imageBox}
                    src={iconStatistics}
                    alt={t('statisticsIcon')}
                    width={70}
                    height={70}
                  />
                </Box>
                {props.id && (
                  <Statistics
                    dashboard={true}
                    stylesText={styles.statisticsText}
                    stylesHistorial={styles.styleHistorialButton}
                    styleInfoBox={styles.styleInfoBox}
                    id={props?.id}
                  />
                )}
                <Box display={'flex'} justifyContent={'flex-end'}>
                  <Box className={styles.updateBox}>
                    <p className={styles.titleUpdateBox}>{t('updated')}</p>
                    <img
                      className={styles.imageCalendar}
                      src={iconCalendar}
                      alt={t('calendarIcon')}
                      width={20}
                      height={20}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} lg={12}>
              <Box className={styles.containerBoxStatistics}>
                <Box className={styles.titleBoxPatientStatistics}>
                  <p className={styles.titleText}>{t('evolution')}</p>
                </Box>
                <FormStatsChart userId={props.id || ''} />
              </Box>
            </Grid>

            {/* #3188 and #3197: protocols applied to the patient and their evaluations */}
            {props.id && canViewProtocols && (
              <Grid item xs={12} lg={12}>
                <PatientProtocols patientID={props.id} editable={canAssignProtocols} />
              </Grid>
            )}
          </Grid>
        </Box>
      </div>
    </PageLayout>
  )
}
